import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface WirelessBssidItem {
  id: string;
  /** Null when the device reports the WLAN without a BSSID (HTTP/JSON
   * management planes typically list SSIDs only). */
  bssid: string | null;
  ssidName: string;
  securityType: string | null;
  vlanId: number | null;
  enabled: boolean;
}

export interface WirelessRadioItem {
  id: string;
  radioIndex: number;
  band: string;
  baseMacAddress: string | null;
  radioName: string | null;
  currentChannel: number | null;
  transmitPowerDbm: number | null;
  status: string;
  bssids: WirelessBssidItem[];
  clientCount: number;
}

export interface WirelessClientItem {
  macAddress: string;
  /** The endpoint this client's MAC resolves to (same org), if any. */
  endpointId: string | null;
  hostname: string | null;
  ouiVendor: string | null;
  ssidName: string | null;
  bssid: string | null;
  band: string | null;
  signalStrengthDbm: number | null;
  signalQuality: number | null;
  snr: number | null;
  associationStatus: string;
}

export interface DeviceWirelessResponse {
  deviceId: string;
  radios: WirelessRadioItem[];
  clients: WirelessClientItem[];
}

export function useDeviceWireless(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceWirelessResponse>({
    queryKey: ['deviceWireless', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<DeviceWirelessResponse>(
        `${config.backendApi}/api/devices/${deviceId}/wireless`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}
